<template>
  <div class="dashboard-wrapper">
    <!-- 1. Header & Filter -->
    <div class="dashboard-header">
      <div class="header-left">
        <h1>Kassa Monitoringi</h1>
        <p class="subtitle">Moliyaviy holat bo'yicha tezkor tahlil</p>
      </div>
      <div class="header-right">
        <div class="date-controls">
          <input type="date" v-model="filter.dateFrom" class="date-input" />
          <span class="separator">-</span>
          <input type="date" v-model="filter.dateTo" class="date-input" />
          <button @click="fetchAll" class="btn-refresh" :disabled="loading || inspectionLoading">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading || inspectionLoading }"></i>
            {{ loading || inspectionLoading ? 'Yuklanmoqda...' : 'Yangilash' }}
          </button>
        </div>
      </div>
    </div>

    <div class="inspection-review">
      <div class="inspection-head">
        <div class="inspection-title">
          <div class="inspection-icon">
            <i class="fas fa-vial"></i>
          </div>
          <div>
            <h2>Tekshiruv Sverka</h2>
            <p>{{ filter.dateFrom }} — {{ filter.dateTo }}</p>
          </div>
        </div>
        <div class="inspection-summary">
          <span>Kirim: {{ formatCurrency(inspectionTotals.total) }}</span>
          <span>{{ inspectionTotals.count }} ta tekshiruv</span>
        </div>
      </div>

      <div class="inspection-tabs" v-if="inspectionCategories.length">
        <button
          v-for="item in inspectionCategories"
          :key="getInspectionCategoryId(item)"
          type="button"
          class="inspection-tab"
          :class="{ active: openedInspectionCategory === getInspectionCategoryId(item) }"
          @click="selectInspectionCategory(item)"
        >
          <span class="tab-letter">{{ getCategoryName(item).charAt(0) }}</span>
          <strong>{{ getCategoryName(item) }}</strong>
          <small>{{ toNumber(item.count) }} ta</small>
          <b>{{ formatPlainCurrency(item.total_kirim) }}</b>
        </button>
      </div>

      <div v-if="inspectionLoading" class="inspection-empty">
        Tekshiruv sverka yuklanmoqda...
      </div>
      <div v-else-if="!inspectionCategories.length" class="inspection-empty">
        Bu davr uchun tekshiruv topilmadi
      </div>

      <div v-else class="inspection-body">
        <div
          v-for="group in openedInspectionGroups"
          :key="group.id"
          class="inspection-accordion"
        >
          <button
            type="button"
            class="inspection-row"
            @click="toggleInspectionGroup(group.id)"
          >
            <span class="row-letter">{{ group.name.charAt(0) }}</span>
            <span class="row-name">{{ group.name }}</span>
            <span class="row-count">{{ group.count }} ta topshirilgan</span>
            <strong>{{ formatCurrency(group.total) }}</strong>
            <i
              class="fas"
              :class="openedInspectionGroup === group.id ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
          </button>

          <div v-if="openedInspectionGroup === group.id" class="patient-table-wrap">
            <table class="patient-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Bemor</th>
                  <th>Telefon</th>
                  <th>Sana</th>
                  <th>Jami narx</th>
                  <th>To'langan</th>
                  <th>Qarz</th>
                  <th>To'lov</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(patient, index) in group.patients" :key="patient.id">
                  <td>{{ index + 1 }}</td>
                  <td class="patient-cell">
                    <span class="patient-letter">{{ getPatientName(patient).charAt(0) }}</span>
                    {{ getPatientName(patient) }}
                  </td>
                  <td>{{ getPatientPhone(patient) || '—' }}</td>
                  <td>{{ formatOnlyDate(patient.date_time) }}</td>
                  <td class="purple">{{ formatCurrency(patient.kirim || patient.price) }}</td>
                  <td class="green">{{ formatCurrency(getPaid(patient)) }}</td>
                  <td>
                    <span :class="getDebt(patient) > 0 ? 'debt-badge' : 'no-debt'">
                      {{ getDebt(patient) > 0 ? formatCurrency(getDebt(patient)) : '—' }}
                    </span>
                  </td>
                  <td>
                    <span class="cash-badge">{{ patient.pay_type || 'Naqt' }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="inspection-footer">
              <div>
                <span>Topshirildi</span>
                <strong>{{ group.count }} ta</strong>
              </div>
              <div>
                <span>Jami narx</span>
                <strong class="purple">{{ formatCurrency(group.total) }}</strong>
              </div>
              <div>
                <span>To'langan</span>
                <strong class="green">{{ formatCurrency(group.paid) }}</strong>
              </div>
              <div>
                <span>Qarzdorlik</span>
                <strong class="red">{{ formatCurrency(group.debt) }}</strong>
              </div>
            </div>
          </div>
        </div>

        <div v-if="inspectionDetailLoading" class="inspection-empty">
          Bemorlar ro'yxati yuklanmoqda...
        </div>
      </div>
    </div>

    <!-- 2. Summary Cards -->
    <div class="cards-grid">
      <!-- Income Card -->
      <div class="stat-card income">
        <div class="card-icon">
          <i class="fas fa-arrow-down"></i>
        </div>
        <div class="card-details">
          <span class="card-label">Umumiy Kirim</span>
          <h2 class="card-value">{{ formatCurrency(summary.total_income) }}</h2>
        </div>
        <div class="card-bg-shape"></div>
      </div>

      <!-- Expense Card -->
      <div class="stat-card expense">
        <div class="card-icon">
          <i class="fas fa-arrow-up"></i>
        </div>
        <div class="card-details">
          <span class="card-label">Xarajatlar</span>
          <h2 class="card-value">{{ formatCurrency(summary.total_expense) }}</h2>
        </div>
        <div class="card-bg-shape"></div>
      </div>

      <!-- Balance Card -->
      <div class="stat-card balance">
        <div class="card-icon">
          <i class="fas fa-wallet"></i>
        </div>
        <div class="card-details">
          <span class="card-label">Joriy Balans</span>
          <h2 class="card-value">{{ formatCurrency(summary.balance) }}</h2>
        </div>
        <div class="card-bg-shape"></div>
      </div>

      <!-- Cash Card (Quick Stat) -->
      <div class="stat-card cash-mini">
        <div class="card-details">
          <span class="card-label">Naqd Puldagi Qoldiq (Taxminiy)</span>
          <h3 class="card-value-sm">{{ formatCurrency(summary.income_cash - summary.expense_cash) }}</h3>
        </div>
        <div class="mini-icon"><i class="fas fa-money-bill-wave"></i></div>
      </div>
    </div>

    <!-- 3. Charts Section -->
    <div class="charts-section">
      <div class="chart-box main-chart">
        <div class="box-header">
          <h3>Kirim va Chiqim Dinamikasi</h3>
        </div>
        <div class="chart-container">
          <canvas id="lineChart"></canvas>
        </div>
      </div>
      
      <div class="chart-box side-chart">
        <div class="box-header">
          <h3>To'lov Turlari</h3>
        </div>
        <div class="doughnut-container">
          <canvas id="doughnutChart"></canvas>
        </div>
        <div class="chart-legend">
          <div class="legend-item">
            <span class="dot cash"></span>
            <span class="label">Naqd</span>
            <span class="val">{{ formatCurrency(summary.income_cash) }}</span>
          </div>
          <div class="legend-item">
            <span class="dot card"></span>
            <span class="label">Plastik</span>
            <span class="val">{{ formatCurrency(summary.income_card) }}</span>
          </div>
          <div class="legend-item">
            <span class="dot click"></span>
            <span class="label">Click</span>
            <span class="val">{{ formatCurrency(summary.income_click) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Recent Transactions Table -->
    <div class="table-section">
      <div class="box-header">
        <h3>So'nggi Operatsiyalar</h3>
      </div>
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Sana va Vaqt</th>
              <th>Bemor / Mijoz</th>
              <th>Bo'lim</th>
              <th>Izoh</th>
              <th>To'lov Turi</th>
              <th>Summa</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in transactions" :key="item.id">
              <td>{{ index + 1 }}</td>
              <td>{{ formatDate(item.date_time) }}</td>
              <td class="patient-name">{{ item.patient ? item.patient.fullname : '—' }}</td>
              <td><span class="badge place">{{ item.place }}</span></td>
              <td class="comment-text" :title="item.comment">{{ item.comment || 'Izoh yo\'q' }}</td>
              <td>
                <span :class="['pay-badge', item.pay_type ? item.pay_type.toLowerCase() : '']">{{ item.pay_type }}</span>
              </td>
              <td :class="['amount', item.doc_type === 'Kirim' ? 'plus' : 'minus']">
                {{ item.doc_type === 'Kirim' ? '+' : '-' }} {{ formatCurrency(item.price) }}
              </td>
            </tr>
            <tr v-if="transactions.length === 0">
              <td colspan="7" class="empty-state">Ma'lumot topilmadi</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
// CHART.JS 2.9.4 UCHUN IMPORT
import Chart from 'chart.js';

export default {
  name: 'KassaDashboard',
  data() {
    return {
      loading: false,
      filter: {
        dateFrom: new Date().toISOString().slice(0, 10), // Bugungi sana
        dateTo: new Date().toISOString().slice(0, 10)
      },
      summary: {
        total_income: 0,
        total_expense: 0,
        balance: 0,
        income_cash: 0,
        expense_cash: 0, // Agar backenddan kelsa
        income_card: 0,
        income_click: 0
      },
      transactions: [],
      inspectionLoading: false,
      inspectionDetailLoading: false,
      inspectionCategories: [],
      inspectionDetails: {},
      openedInspectionCategory: null,
      openedInspectionGroup: null,
      lineChartInstance: null,
      doughnutChartInstance: null
    };
  },
  computed: {
    inspectionTotals() {
      return this.inspectionCategories.reduce((total, item) => {
        total.count += this.toNumber(item.count);
        total.total += this.toNumber(item.total_kirim);
        return total;
      }, { count: 0, total: 0 });
    },
    openedInspectionGroups() {
      return this.inspectionDetails[this.openedInspectionCategory] || [];
    }
  },
  methods: {
    toNumber(value) {
      return Number(value) || 0;
    },
    formatCurrency(value) {
      if (!value) return "0 so'm";
      return new Intl.NumberFormat('uz-UZ').format(value) + " so'm";
    },
    formatPlainCurrency(value) {
      return new Intl.NumberFormat('uz-UZ').format(this.toNumber(value));
    },
    formatDate(timestamp) {
      // Backenddan timestamp sekundda kelsa * 1000
      return new Date(timestamp * 1000).toLocaleString('uz-UZ', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
      });
    },
    formatOnlyDate(timestamp) {
      if (!timestamp) return '—';
      return new Date(timestamp * 1000).toLocaleDateString('uz-UZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    getInspectionCategoryId(item) {
      return item["inspection.id"] || (item.inspection && item.inspection.id) || item.inspection_category;
    },
    getCategoryName(item) {
      return item["inspection.name"] || (item.inspection && item.inspection.name) || 'Tekshiruv';
    },
    getInspectionName(item) {
      return item.inspection_name ||
        item["inspec.name"] ||
        (item.inspec && item.inspec.name) ||
        (item.inspection && item.inspection.name) ||
        item.comment ||
        'Tekshiruv';
    },
    getPatientName(item) {
      return item.patient_name ||
        (item.patient && item.patient.fullname) ||
        (item.registration && item.registration.patient && item.registration.patient.fullname) ||
        'Noma\'lum bemor';
    },
    getPatientPhone(item) {
      return item.patient_phone ||
        (item.patient && item.patient.phone) ||
        (item.registration && item.registration.patient && item.registration.patient.phone) ||
        '';
    },
    getPaid(item) {
      if (item.pay_summa !== undefined) {
        return this.toNumber(item.pay_summa);
      }
      if (item.registration && item.registration.pay_summa !== undefined) {
        return this.toNumber(item.registration.pay_summa);
      }
      return this.toNumber(item.kirim);
    },
    getDebt(item) {
      if (item.backlog !== undefined) {
        return this.toNumber(item.backlog);
      }
      if (item.registration && item.registration.backlog !== undefined) {
        return this.toNumber(item.registration.backlog);
      }
      return this.toNumber(item.Chiqim);
    },
    async fetchAll() {
      await Promise.all([
        this.fetchDashboard(),
        this.fetchInspectionReview()
      ]);
    },
    async fetchDashboard() {
      this.loading = true;
      try {
        // Backend sekundda (UNIX timestamp) kutayotgan bo'lsa:
        const datetime1 = Math.floor(new Date(this.filter.dateFrom).setHours(0, 0, 0, 0) / 1000);
        const datetime2 = Math.floor(new Date(this.filter.dateTo).setHours(23, 59, 59, 999) / 1000);

        const response = await axios.post('hisobot/kassa-dashboard', {
          datetime1,
          datetime2 
        });  
        if (!response.data.error) {
          const { summary, chart_data, transactions } = response.data.data;
          this.summary = summary;
          this.transactions = transactions;
          
          this.updateCharts(chart_data);
        }
      } catch (error) {
        console.error("Dashboard yuklashda xatolik:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchInspectionReview() {
      this.inspectionLoading = true;
      try {
        const datetime1 = Math.floor(new Date(this.filter.dateFrom).setHours(0, 0, 0, 0) / 1000);
        const datetime2 = Math.floor(new Date(this.filter.dateTo).setHours(23, 59, 59, 999) / 1000);
        const response = await axios.post('hisobot/inspection_hisobot', {
          datetime1,
          datetime2,
          inspection_category: null
        });
        if (!response || !response.data) {
          this.inspectionCategories = [];
          this.inspectionDetails = {};
          return;
        }
        const rows = response.data && response.data.data ? response.data.data : response.data;
        this.inspectionCategories = Array.isArray(rows) ? rows : [];
        this.inspectionDetails = {};

        if (this.inspectionCategories.length) {
          await this.selectInspectionCategory(this.inspectionCategories[0]);
        } else {
          this.openedInspectionCategory = null;
          this.openedInspectionGroup = null;
        }
      } catch (error) {
        console.error("Tekshiruv sverka yuklashda xatolik:", error);
      } finally {
        this.inspectionLoading = false;
      }
    },
    async selectInspectionCategory(item) {
      const categoryId = this.getInspectionCategoryId(item);
      this.openedInspectionCategory = categoryId;
      this.openedInspectionGroup = null;

      if (this.inspectionDetails[categoryId]) {
        return;
      }

      this.inspectionDetailLoading = true;
      try {
        const datetime1 = Math.floor(new Date(this.filter.dateFrom).setHours(0, 0, 0, 0) / 1000);
        const datetime2 = Math.floor(new Date(this.filter.dateTo).setHours(23, 59, 59, 999) / 1000);
        const response = await axios.post('hisobot/patient_revision', {
          datetime1,
          datetime2,
          category_id: categoryId
        });
        if (!response || !response.data) {
          this.$set(this.inspectionDetails, categoryId, []);
          return;
        }
        const rows = response.data.inspections || response.data.data || response.data;
        const groups = this.groupInspectionPatients(Array.isArray(rows) ? rows : []);
        this.$set(this.inspectionDetails, categoryId, groups);
        if (groups.length) {
          this.openedInspectionGroup = groups[0].id;
        }
      } catch (error) {
        console.error("Tekshiruv bemorlari yuklashda xatolik:", error);
        this.$set(this.inspectionDetails, categoryId, []);
      } finally {
        this.inspectionDetailLoading = false;
      }
    },
    groupInspectionPatients(rows) {
      const grouped = rows.reduce((acc, item) => {
        const id = item.inspection_id || this.getInspectionName(item);
        if (!acc[id]) {
          acc[id] = {
            id,
            name: this.getInspectionName(item),
            count: 0,
            total: 0,
            paid: 0,
            debt: 0,
            patients: []
          };
        }
        acc[id].count += 1;
        acc[id].total += this.toNumber(item.kirim || item.price);
        acc[id].paid += this.getPaid(item);
        acc[id].debt += this.getDebt(item);
        acc[id].patients.push(item);
        return acc;
      }, {});
      return Object.values(grouped).sort((a, b) => b.total - a.total);
    },
    toggleInspectionGroup(groupId) {
      this.openedInspectionGroup = this.openedInspectionGroup === groupId ? null : groupId;
    },
    updateCharts(chartData) {
      // -------------------------------
      // 1. Line Chart (Kirim/Chiqim) - CHART.JS v2 SYNTAX
      // -------------------------------
      const ctxLine = document.getElementById('lineChart').getContext('2d');
      if (this.lineChartInstance) this.lineChartInstance.destroy();

      const labels = chartData.map(d => d.date);
      const incomeData = chartData.map(d => d.daily_income);
      const expenseData = chartData.map(d => d.daily_expense);

      this.lineChartInstance = new Chart(ctxLine, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'Kirim',
              data: incomeData,
              borderColor: '#10B981',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              lineTension: 0.3, // v2 da tension o'rniga
              fill: true,
              pointRadius: 4,
              pointHoverRadius: 6
            },
            {
              label: 'Chiqim',
              data: expenseData,
              borderColor: '#EF4444',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              lineTension: 0.3,
              fill: true,
              pointRadius: 4,
              pointHoverRadius: 6
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { 
            position: 'top',
            labels: { fontColor: '#64748B' } 
          },
          tooltips: {
            mode: 'index',
            intersect: false,
          },
          scales: {
            yAxes: [{ // v2 Syntax
              ticks: { 
                beginAtZero: true,
                fontColor: '#94A3B8'
              },
              gridLines: { color: '#F1F5F9' }
            }],
            xAxes: [{ // v2 Syntax
              ticks: { fontColor: '#94A3B8' },
              gridLines: { display: false }
            }]
          }
        }
      });

      // -------------------------------
      // 2. Doughnut Chart (To'lov turlari) - CHART.JS v2 SYNTAX
      // -------------------------------
      const ctxDoughnut = document.getElementById('doughnutChart').getContext('2d');
      if (this.doughnutChartInstance) this.doughnutChartInstance.destroy();

      this.doughnutChartInstance = new Chart(ctxDoughnut, {
        type: 'doughnut',
        data: {
          labels: ['Naqd', 'Plastik', 'Click'],
          datasets: [{
            data: [this.summary.income_cash, this.summary.income_card, this.summary.income_click],
            backgroundColor: ['#3B82F6', '#8B5CF6', '#F59E0B'],
            hoverBackgroundColor: ['#2563EB', '#7C3AED', '#D97706'],
            borderWidth: 2,
            borderColor: '#ffffff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutoutPercentage: 75, // v2 Syntax (cutout o'rniga)
          legend: { display: false },
          tooltips: {
            callbacks: {
              label: function(tooltipItem, data) {
                var label = data.labels[tooltipItem.index] || '';
                var value = data.datasets[0].data[tooltipItem.index];
                return label + ': ' + new Intl.NumberFormat('uz-UZ').format(value);
              }
            }
          }
        }
      });
    }
  },
  mounted() {
    this.fetchAll();
  }
};
</script>

<style scoped>
/* Font importi: Agar loyihangizda bo'lmasa */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* Main Wrapper */
.dashboard-wrapper {
  padding: 30px;
  background-color: #F8FAFC;
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
  color: #1E293B;
}

/* Header */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
}
.header-left h1 {
  font-size: 26px;
  font-weight: 700;
  color: #0F172A;
  margin: 0;
}
.subtitle {
  color: #64748B;
  margin-top: 5px;
  font-size: 14px;
}
.date-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  padding: 8px 15px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}
.date-input {
  border: 1px solid #E2E8F0;
  padding: 8px 12px;
  border-radius: 8px;
  color: #334155;
  outline: none;
  font-family: inherit;
}
.btn-refresh {
  background: #3B82F6;
  color: white;
  border: none;
  padding: 9px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}
.btn-refresh:hover {
  background: #2563EB;
  transform: translateY(-1px);
}
.btn-refresh:disabled {
  background: #93C5FD;
  cursor: not-allowed;
}

.inspection-review {
  background: #fff;
  border-left: 4px solid #7C3AED;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
  margin-bottom: 30px;
  overflow: hidden;
}
.inspection-head {
  align-items: center;
  border-bottom: 1px solid #E2E8F0;
  display: flex;
  justify-content: space-between;
  padding: 22px 24px;
  gap: 16px;
}
.inspection-title {
  align-items: center;
  display: flex;
  gap: 14px;
}
.inspection-icon,
.tab-letter,
.row-letter,
.patient-letter {
  align-items: center;
  background: #F1EAFE;
  color: #6D28D9;
  display: inline-flex;
  font-weight: 700;
  justify-content: center;
}
.inspection-icon {
  border-radius: 12px;
  height: 44px;
  width: 44px;
}
.inspection-title h2 {
  color: #0F172A;
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}
.inspection-title p {
  color: #64748B;
  font-size: 13px;
  margin: 5px 0 0;
}
.inspection-summary {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.inspection-summary span {
  background: #F5F3FF;
  border-radius: 999px;
  color: #6D28D9;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 14px;
}
.inspection-tabs {
  align-items: center;
  border-bottom: 1px solid #E2E8F0;
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding: 14px 24px;
}
.inspection-tab {
  align-items: center;
  background: transparent;
  border: 0;
  color: #0F172A;
  cursor: pointer;
  display: inline-flex;
  gap: 9px;
  padding: 8px 10px;
  white-space: nowrap;
}
.inspection-tab.active {
  background: #F5F3FF;
  border-radius: 8px;
}
.tab-letter {
  border-radius: 50%;
  height: 30px;
  min-width: 30px;
}
.inspection-tab small {
  background: #EEF2F7;
  border-radius: 999px;
  color: #64748B;
  font-weight: 700;
  padding: 7px 10px;
}
.inspection-tab b,
.inspection-row strong,
.purple {
  color: #6D28D9;
}
.inspection-body {
  padding: 0 24px 18px;
}
.inspection-accordion {
  margin-top: 10px;
}
.inspection-row {
  align-items: center;
  background: #F4F1FC;
  border: 1px solid #E6DDFB;
  border-radius: 8px;
  cursor: pointer;
  display: grid;
  gap: 12px;
  grid-template-columns: 34px minmax(220px, 1fr) auto auto 18px;
  padding: 12px 16px;
  text-align: left;
  width: 100%;
}
.row-letter,
.patient-letter {
  border-radius: 8px;
  height: 34px;
  width: 34px;
}
.row-name {
  color: #0F172A;
  font-weight: 700;
}
.row-count {
  background: #EDE7FE;
  border-radius: 999px;
  color: #6D28D9;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 12px;
}
.patient-table-wrap {
  border: 1px solid #E2E8F0;
  border-radius: 0 0 8px 8px;
  overflow-x: auto;
}
.patient-table th {
  background: #F8FAFC;
  text-transform: none;
}
.patient-table td {
  background: #fff;
}
.patient-cell {
  color: #0F172A;
  font-weight: 700;
  min-width: 260px;
}
.patient-letter {
  height: 30px;
  margin-right: 8px;
  width: 30px;
}
.green {
  color: #059669;
  font-weight: 700;
}
.red {
  color: #EF4444;
}
.debt-badge {
  background: #FEE2E2;
  border-radius: 999px;
  color: #EF4444;
  display: inline-block;
  font-weight: 700;
  padding: 8px 12px;
}
.no-debt {
  color: #059669;
  font-weight: 700;
}
.cash-badge {
  background: #DCFCE7;
  border-radius: 999px;
  color: #047857;
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 8px 12px;
}
.inspection-footer {
  background: #FAF7FF;
  border-top: 1px solid #E6DDFB;
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
}
.inspection-footer div {
  border-right: 1px solid #E6DDFB;
  padding: 16px;
}
.inspection-footer div:last-child {
  border-right: 0;
}
.inspection-footer span {
  color: #94A3B8;
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 8px;
  text-transform: uppercase;
}
.inspection-empty {
  color: #64748B;
  font-weight: 600;
  padding: 26px;
  text-align: center;
}
@media (max-width: 768px) {
  .inspection-head {
    align-items: flex-start;
    flex-direction: column;
  }
  .inspection-row {
    grid-template-columns: 34px 1fr;
  }
  .row-count,
  .inspection-row strong,
  .inspection-row i {
    grid-column: 2;
  }
  .inspection-footer {
    grid-template-columns: 1fr;
  }
  .inspection-footer div {
    border-right: 0;
  }
}

/* Cards Grid */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
  margin-bottom: 30px;
}

.stat-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 20px;
  transition: transform 0.2s, box-shadow 0.2s;
}
.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.card-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  z-index: 2;
}
.stat-card.income .card-icon { background: #ECFDF5; color: #10B981; }
.stat-card.expense .card-icon { background: #FEF2F2; color: #EF4444; }
.stat-card.balance .card-icon { background: #EFF6FF; color: #3B82F6; }
.stat-card.cash-mini { background: #3B82F6; color: white; justify-content: space-between; }

.card-details {
  z-index: 2;
}
.card-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #64748B;
  margin-bottom: 4px;
}
.stat-card.cash-mini .card-label { color: #DBEAFE; }

.card-value {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: #0F172A;
}
.card-value-sm {
    font-size: 18px;
    font-weight: 700;
    margin: 0;
}

/* Decorative Background Shape */
.card-bg-shape {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: currentColor;
  opacity: 0.05;
  border-radius: 50%;
  z-index: 1;
}
.stat-card.income { color: #10B981; }
.stat-card.expense { color: #EF4444; }
.stat-card.balance { color: #3B82F6; }

/* Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-bottom: 30px;
}
@media (max-width: 992px) {
  .charts-section { grid-template-columns: 1fr; }
}

.chart-box {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}
.box-header {
  margin-bottom: 20px;
  border-bottom: 1px solid #F1F5F9;
  padding-bottom: 12px;
}
.box-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #0F172A;
}

.chart-container {
  height: 320px;
  position: relative;
}
.doughnut-container {
  height: 220px;
  position: relative;
  margin-bottom: 15px;
}

.chart-legend {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.legend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
}
.legend-item .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
}
.legend-item .label { flex: 1; color: #475569; }
.legend-item .val { font-weight: 600; color: #0F172A; }
.dot.cash { background: #3B82F6; }
.dot.card { background: #8B5CF6; }
.dot.click { background: #F59E0B; }

/* Table Section */
.table-section {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}
.table-container {
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
th {
  text-align: left;
  padding: 14px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #64748B;
  background: #F8FAFC;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
th:first-child { border-top-left-radius: 8px; }
th:last-child { border-top-right-radius: 8px; }

td {
  padding: 16px;
  font-size: 14px;
  color: #334155;
  border-bottom: 1px solid #F1F5F9;
  vertical-align: middle;
}
tr:last-child td { border-bottom: none; }
tr:hover td { background-color: #F8FAFC; }

.patient-name {
  font-weight: 500;
  color: #0F172A;
}

.badge.place {
  background: #EEF2FF;
  color: #4F46E5;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.comment-text {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #94A3B8;
}

.pay-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 20px;
  text-transform: capitalize;
}
.pay-badge.naqt { color: #3B82F6; background: #EFF6FF; }
.pay-badge.plastik { color: #8B5CF6; background: #F5F3FF; }
.pay-badge.click { color: #D97706; background: #FFFBEB; }

.amount { font-weight: 600; white-space: nowrap; }
.amount.plus { color: #10B981; }
.amount.minus { color: #EF4444; }

.empty-state {
  text-align: center;
  padding: 30px;
  color: #94A3B8;
  font-style: italic;
}
</style>
